import { action, computed, observable, reaction, makeObservable } from 'mobx';
import {TimeZone, timeZoneLoaders, User, userLoaders} from '../../../domain';
import {Disposers, HttpPromiseState, toastrHelper} from '../../../lib/helpers';
import {routes} from '../../../pages/Routes';
import {modalMessageService} from '../../../lib/components/modal/ModalMessageService';
import {DeleteUserStore} from './parts/DeleteUserStore';
import {SetPasswordStore} from './parts/SetPasswordStore';
import {SetTimeZoneStore} from './parts/SetTimezoneStore';


export class UserViewSceneStore {
  @observable
  mode: 'view' | 'set-password' | 'set-timezone' | 'delete';

  @observable
  redirectTo: string | null;

  @observable.ref
  userState: HttpPromiseState<User> | null;

  @observable.ref
  timeZonesState: HttpPromiseState<readonly TimeZone[]> | null;

  @observable.ref
  setPassword: SetPasswordStore | null;
  @observable.ref
  setTimeZone: SetTimeZoneStore | null;
  @observable.ref
  deleteUser: DeleteUserStore | null;

  private readonly disposers = new Disposers();

  constructor(readonly userId: number) {
    makeObservable(this);
    this.mode = 'view';
    this.redirectTo = null;
    this.userState = this.timeZonesState = null;
    this.setPassword = this.setTimeZone = this.deleteUser = null;
    this.setup();
  }

  @action
  toViewMode = () => {
    this.mode = 'view';
    this.setPassword = this.setTimeZone = this.deleteUser = null;
  };

  @action
  cancel = () => {
    this.toViewMode();
  };

  @action
  loadUser = () => {
    if (this.userState?.isPending === true)
      return;
    this.userState = userLoaders.get(this.userId);
  };

  @computed
  get user(): User | null {
    if (this.userState?.isResolved !== true) return null;
    return this.userState.value;
  }

  @action
  loadTimeZones = () => {
    if (this.timeZonesState?.isPending === true)
      return;
    this.timeZonesState = timeZoneLoaders.list();
  };

  /****************************************************************************/

  @action
  beginSetPassword = () => {
    if (this.userState?.isResolved !== true)
      return;
    this.mode = 'set-password';
    this.setPassword = new SetPasswordStore(this.userId);
  };

  @action
  completeSetPassword = () => {
    this.setPassword?.complete();
  };

  /****************************************************************************/

  @action
  beginSetTimeZone = () => {
    if (this.timeZonesState?.isResolved !== true)
      this.loadTimeZones();

    this.mode = 'set-timezone';
    this.setTimeZone = new SetTimeZoneStore(this.userId);
  };

  @action
  completeSetTimeZone = () => {
    this.setTimeZone?.complete();
  };

  /****************************************************************************/

  @action
  confirmThenDelete = () => {
    modalMessageService.prompt({
      message: 'Are you sure you want to delete user?',
      confirmLabel: 'Delete',
      confirmAction: this.beginDelete,
      cancelAction: this.cancel
    });
  };

  @action
  beginDelete = () => {
    this.mode = 'delete';
    this.deleteUser = new DeleteUserStore(this.userId);
    this.completeDelete();
  };

  @action
  completeDelete = () => {
    this.deleteUser?.complete();
  };

  /****************************************************************************/


  private setup() {
    this.disposers.add(
      reaction(
        () => [this.mode, this.userState, this.timeZonesState?.state],
        this.initTimeZoneStore),
      reaction(
        () => [
          this.setPassword?.save?.state,
          this.setTimeZone?.save?.state],
        this.afterUserUpdated,
      ),
      reaction(
        () => [this.deleteUser?.save?.state],
        this.afterUserDeleted));
  }

  @action
  private initTimeZoneStore = () => {
    if (this.userState?.isResolved !== true || this.timeZonesState?.isResolved !== true)
      return;
    this.setTimeZone?.init(this.userState.value, this.timeZonesState.value);
  };

  @action
  private afterUserUpdated = () => {
    for (let state of [this.setPassword?.save, this.setTimeZone?.save])
      if (state?.isResolved === true) {
        toastrHelper.success(state.value);
        this.toViewMode();
        this.loadUser();
      }
  };

  @action
  private afterUserDeleted = () => {
    if (this.deleteUser?.save?.isResolved !== true)
      return;
    toastrHelper.success(this.deleteUser.save.value);
    this.redirectTo = routes.UserList;
  };

  dispose = () => {
    this.disposers.dispose();
  };
}
